import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LinkAsButton } from "@/components/LinkAsButton";
import { EntityListPage } from "@/components/entity-page/EntityListPage";
import { getFormOptions } from "@/lib/api";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/settings/voices/")({
  component: VoicesIndexPage,
});

function VoicesIndexPage() {
  return <VoicesIndexPageComponent />;
}

// Exported for testing
export function VoicesIndexPageComponent() {
  const [voices, setVoices] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getFormOptions("voices")
      .then((options) => {
        if (!active) return;
        setVoices(options);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching voices:", err);
        setError("Failed to load voices.");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <EntityListPage
      title="Voices"
      description="The TTS voices agents can speak with, including provider-specific voice identifiers - each option here populates an agent's voiceId field."
      items={voices}
      loading={loading}
      error={error}
      getItemKey={(voice) => voice.id}
      gridClassName="grid grid-cols-1 gap-4"
      renderItem={(voice) => (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle>{voice.label}</CardTitle>
                <CardDescription>{voice.description || `Value: ${voice.value}`}</CardDescription>
                <p className="text-xs text-muted-foreground mt-1">Order: {voice.order ?? 0}</p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard/settings/voices/$id" params={{ id: voice.id }}>
                  Edit
                </Link>
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}
      createHref="/dashboard/settings/voices/new"
      createLabel="Create Voice"
      emptyMessage="No voices yet. Create one to get started."
      headerActions={
        <LinkAsButton to="/dashboard/settings" variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </LinkAsButton>
      }
    />
  );
}
